import React, { useState, useEffect, useRef } from 'react';
import { View, Animated, StyleSheet, Text } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

import { scale as initialScale } from '@components/frame';
import { isPhone, isPhoneNative, isTabletOrWeb, isWeb, convertTimeLetter, navigateBack, Alert } from '@helpers';

import ColorWrapper from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';
import LevelSelector from '@styles/selectors/level';
import { font as initialFont } from '@styles/fonts';

import EditableLine from '@components/common/editableLine';
import Loading from '@components/loading';

import Header from './components/Header';
import NCFile from './components/NCFile';

import { updateMachinePI, getMachine } from '@reducers/machines';
import { setMachineDetailsLevel } from '@reducers/dynamicConfig';
import { addImage, clearState } from '@reducers/imageHandling';
import { startQueue } from '@reducers/uploadQueue';
import { addProductionInformation, getEvent, getNCFileById, addProductionInformationV2 } from '@reducers/manufacture';
import { getTools } from '@reducers/clampingTools';
import { getPartsSearch } from '@reducers/parts';
import { partsSearch } from '@reducers/search';

import { partFields, timeTypeConfig, pieceTimeConfig, batchTimeConfig } from './config';

import ClampingTool from '@components/clampingTool';
import Selector from '@styles/selectors';
import LineSelector from '@components/common/lineSelector';
import ToggleSwitch from 'toggle-switch-react-native';
import { modalBg, modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { SEARCH_PAGE_SIZE } from './baseWithERP/constants';

let loadPartTimeout = null;
let loadDrawingTimeout = null;

const PartBlock = ({ data, setData, updateData, openKey, getPartsSearch, C, isPhone, scale = initialScale, font = initialFont }) => {
  const [isNew, setIsNew] = useState(false);
  const [showNewButton, setShowNewButton] = useState(!data.part.partId);
  const [autocompleteList, setAutocompleteList] = useState({
    partNumber: [],
    partDrawingNumber: [],
  });

  const [loadingAutocomplete, setLoadingAutocomplete] = useState({
    partNumber: false,
    partDrawingNumber: false,
  });

  const autocomplete = {
    partNumber: true,
    partDrawingNumber: true,
  };

  const loadParts = async (value) => {
    if (!value) {
      return;
    }

    const list = await getPartsSearch(value);
    setAutocompleteList({
      ...autocompleteList,
      partNumber: list.searchResult.map((i) => ({
        id: i.partId,
        topLine: i.partName,
        middleLine: i.description,
        bottomLine: i.drawing,
        ...i,
      })),
    });
    setLoadingAutocomplete({
      ...loadingAutocomplete,
      partNumber: false,
    });
  };

  const loadDrawing = async (value) => {
    if (!value) {
      return;
    }

    const list = await getPartsSearch(value);
    setAutocompleteList({
      ...autocompleteList,
      partDrawingNumber: list.searchResult.map((i) => ({
        id: i.partId,
        topLine: i.drawing,
        middleLine: i.description,
        bottomLine: i.partName,
        ...i,
      })),
    });
    setLoadingAutocomplete({
      ...loadingAutocomplete,
      partDrawingNumber: false,
    });
  };

  const updateDataPart = (key, value) => {
    setData({
      ...data,
      part: {
        ...data.part,
        [key]: value,
      },
    });
  };

  return (
    <View style={{ width: '100%', paddingHorizontal: scale(18) }}>
      <View
        style={[
          getBoxShadowStyle({ type: 'Field', C }),
          { borderRadius: scale(SCREEN_BORDER_RADIUS), zIndex: 1000 },
          isPhone && {
            borderTopWidth: StyleSheet.hairlineWidth,
            borderTopColor: C('Hairline/Light/No trans'),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: C('Hairline/Light/No trans'),
          },
        ]}
      >
        {partFields.map((item, index) => {
          return (
            <EditableLine
              {...{
                scale,
                font,
                item: {
                  value: data.part[item.key],
                  onChange: (v) => {
                    updateDataPart(item.key, v);

                    if (loadPartTimeout) {
                      clearTimeout(loadPartTimeout);
                    }
                    if (loadDrawingTimeout) {
                      clearTimeout(loadDrawingTimeout);
                    }
                    if (item.key === 'partNumber') {
                      setShowNewButton(true);
                      setLoadingAutocomplete({
                        ...loadingAutocomplete,
                        partNumber: true,
                      });
                      loadPartTimeout = setTimeout(() => {
                        loadParts(v);
                      }, 1000);
                    }
                    if (item.key === 'partDrawingNumber') {
                      setShowNewButton(true);
                      setLoadingAutocomplete({
                        ...loadingAutocomplete,
                        partDrawingNumber: true,
                      });
                      loadDrawingTimeout = setTimeout(() => {
                        loadDrawing(v);
                      }, 1000);
                    }
                  },
                  onClick: () => openKey(item.key),
                  showNewButton: showNewButton,
                  onNewPress: () => {
                    setIsNew(true);
                    setData({
                      ...data,
                      part: {
                        partId: null,
                      },
                    });
                  },
                  autocompleteLoading: loadingAutocomplete[item.key],
                  autocomplete: autocomplete[item.key] && !isNew,
                  autocompleteList: autocompleteList[item.key],
                  onAutocompletePress: (i) => {
                    setIsNew(false);
                    setShowNewButton(false);

                    setData({
                      ...data,
                      part: {
                        partId: i.partId,
                        partDrawingPosition: i.position,
                        partNumber: i.partName,
                        partDrawingNumber: i.drawing,
                        partDescription: i.description,
                        partDrawingRevision: i.revision,
                      },
                    });

                    setAutocompleteList({
                      partNumber: [],
                      partDrawingNumber: [],
                    });
                  },
                  clearButtonMode: 'never',
                  ...item,
                },
                index,
                array: partFields,
                C,
                bottomBorderBox: false,
                topBorder: index !== 0,
                roundTop: isPhone ? false : index == 0,
                roundBottom: isPhone ? false : index === partFields.length - 1,
              }}
              key={item.name}
            />
          );
        })}
      </View>
      <View>
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), paddingHorizontal: scale(16), marginTop: scale(6) }]}>
          {i18n.t('If you add Part Information, either the field Part or Drawing must be filled')}
        </Text>
      </View>
    </View>
  );
};

const NCFileBlock = ({ data, updateData, setData, C, openKey, animation, scale = initialScale, font = initialFont }) => {
  const [selectedTab, setSelectedTab] = useState('ideal');

  const dataScope = data[selectedTab] || {};

  const isPaidTime = selectedTab === 'paid';

  const tabs = [
    {
      name: 'Ideal Time',
      key: 'ideal',
    },
    {
      name: 'Paid Time',
      key: 'paid',
    },
    // {
    //   name: 'Production Information',
    //   key: 'productionInformation',
    // },
  ];

  const updateDataScope = (key, value) => {
    setData({
      ...data,
      [selectedTab]: {
        ...data[selectedTab],
        [key]: value,
      },
    });
  };

  const labelStorage = (() => {
    switch (selectedTab) {
      case 'ideal':
        return {
          timeTypeDescription: 'The fastest times will automatically become the target time.',
          pieceHeader: 'IDEAL TIME PER PIECE',
          pieceDescription: 'You can add time like 1.5h, 1h 30m, 01:30:00, 5400s, 50m + 40m',
          batchHeader: 'IDEAL TIME PER BATCH',
        };
      case 'paid':
        return {
          timeTypeDescription: 'The fastest times will automatically become the Paid time..',
          pieceHeader: 'PAID TIME PER PIECE',
          pieceDescription: 'You can add time like 1.5h, 1h 30m, 01:30:00, 5400s, 50m + 40m',
          batchHeader: 'PAID TIME PER BATCH',
        };
      default: {
        return {};
      }
    }
  })();

  return (
    <View style={{ flex: 1, paddingHorizontal: scale(18) }}>
      <View style={{ flexDirection: 'row', justifyContent: 'center', marginBottom: scale(25) }}>
        {tabs.map((item) => (
          <View key={item.key}>
            <Selector
              {...{
                scale,
                font,
                selected: selectedTab === item.key,
                variant: 'L',
                text: i18n.t(item.name),
                onPress: () => {
                  setSelectedTab(item.key);
                },
              }}
            />
          </View>
        ))}
      </View>
      {isPaidTime && (
        <View style={{ marginBottom: scale(20) }}>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              height: scale(42),
              backgroundColor: C(modalFieldBg),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              paddingLeft: scale(15),
              paddingRight: scale(11),
              justifyContent: 'space-between',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text style={[font['Subhead 1'], { color: C('Label/Light/Primary') }]}>{i18n.t('Paid Time = Ideal Time')}</Text>
            <ToggleSwitch
              size="medium"
              onToggle={(v) => updateData('paidEqualIdeal', v)}
              isOn={data.paidEqualIdeal}
              animationSpeed={100}
              offColor={C('Fill/Light/Tertiary')}
            />
          </View>
        </View>
      )}
      <View style={{ marginBottom: scale(26) }}>
        <View>
          {timeTypeConfig.map((item, index) => (
            <LineSelector
              {...{
                scale,
                font,
                key: item.key,
                C,
                text: item.name,
                onPress: () => {
                  updateDataScope('timeType', item.key);
                },
                selected: item.key === dataScope.timeType,
                borderRadiusTop: index === 0,
                borderRadiusBottom: index === timeTypeConfig.length - 1,
                shadow: false,
                borderBottom: index !== timeTypeConfig.length - 1,
                bg: modalFieldBg,
              }}
              key={item.key}
            />
          ))}
        </View>
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), paddingHorizontal: scale(16), marginTop: scale(6) }]}>
          {i18n.t(labelStorage.timeTypeDescription)}
        </Text>
      </View>
      <View style={{ marginBottom: scale(26) }}>
        <Text
          style={[
            font['Caption 1'],
            { color: C('Label/Light/Tertiary'), textTransform: 'capitalize', paddingHorizontal: scale(16), marginBottom: scale(6) },
          ]}
        >
          {i18n.t(labelStorage.pieceHeader)}
        </Text>
        {pieceTimeConfig.map((item, index) => (
          <EditableLine
            {...{
              scale,
              font,
              item: {
                value: dataScope[item.key],
                onChange: (v) => updateDataScope(item.key, v),
                onClick: () => openKey(item.key),
                animatedHeight: animation[item.key],
                justifyContent: 'flex-end',
                paddingRight: scale(20),
                isOffset: false,
                small: false,
                ...item,
              },
              bg: modalFieldBg,
              index,
              array: pieceTimeConfig,
              C,
              bottomBorderBox: false,
              topBorderBox: false,
              topBorder: index !== 0,
              roundTop: index === 0,
              roundBottom: index === pieceTimeConfig.length - 1,
            }}
            key={item.key + selectedTab}
          />
        ))}
        <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), paddingHorizontal: scale(16), marginTop: scale(6) }]}>
          {i18n.t(labelStorage.pieceDescription)}
        </Text>
      </View>
      <View>
        <Text
          style={[
            font['Caption 1'],
            { color: C('Label/Light/Tertiary'), textTransform: 'capitalize', paddingHorizontal: scale(16), marginBottom: scale(6) },
          ]}
        >
          {i18n.t(labelStorage.batchHeader)}
        </Text>
        {batchTimeConfig.map((item, index) => (
          <EditableLine
            {...{
              scale,
              font,
              item: {
                value: dataScope[item.key],
                onChange: (v) => updateDataScope(item.key, v),
                onClick: () => openKey(item.key),
                animatedHeight: animation[item.key],
                justifyContent: 'flex-end',
                paddingRight: scale(20),
                isOffset: false,
                small: false,
                ...item,
              },
              bg: modalFieldBg,
              index,
              array: batchTimeConfig,
              C,
              bottomBorderBox: false,
              topBorderBox: false,
              topBorder: index !== 0,
              roundTop: index === 0,
              roundBottom: index === batchTimeConfig.length - 1,
            }}
            key={item.key}
          />
        ))}
      </View>
    </View>
  );
};

const Modal = React.memo((props) => {
  const {
    C,
    route = {},
    getTools,
    getPartsSearch,
    onBackPress = navigateBack,
    hide = navigateBack,
    getNCFileById,
    addProductionInformationV2,
    partId,
    partsSearch,
    selectedNCFileId,
    mainBg,
    isOperatorPanel,
    isNcFileContentAvailable,
    shouldTakeNCFileDataFromMachine,
    onActionStart,
    scale = initialScale,
    font = initialFont,
  } = props;

  let level = route?.params?.level;

  const [selectedLevel, setSelectedLevel] = useState(level || 'ncFile');

  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState({});
  const [tools, setTools] = useState();
  const [lockSave, setLockSave] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const formatTimeToSeconds = (t) => Number(t.h) * 60 * 60 + Number(t.m) * 60 + Number(t.s);
  const saveData = async () => {
    if (lockSave) {
      return;
    }
    setLockSave(true);

    if (onActionStart) {
      onActionStart();
    }

    const response = await addProductionInformationV2({
      ncFileId: data.ncFileId,
      // clampingDetails: {
      //   runTimePerPcsSecond: formatTimeToSeconds(data.ideal.runtime),
      //   changeTimePerPcsSecond: formatTimeToSeconds(data.ideal.changeTime),
      //   setupTimeSecond: formatTimeToSeconds(data.ideal.setupTime),
      //   useManualIdealTimes: data.ideal.timeType === 'manual',
      //   paidRunTimePerPcsSecond: formatTimeToSeconds(data.paid.runtime),
      //   paidChangeTimePerPcsSecond: formatTimeToSeconds(data.paid.changeTime),
      //   paidSetupTimeSecond: formatTimeToSeconds(data.paid.setupTime),
      //   useManualPaidTimes: data.paid.timeType === 'manual',
      //   paidTimesEqualsIdealTimes: data.paidEqualIdeal,
      //   // memo: memo text
      //   // setupSheet: the name of the setup sheet
      // },
      partId: data.part.partId,
      partDetails: {
        partName: data.part.partNumber,
        description: data.part.partDescription,
        drawing: data.part.partDrawingNumber,
        revision: data.part.partDrawingRevision,
        position: data.part.partDrawingPosition,
      },
    });

    if (response.error) {
      Alert({ title: response?.errorDetails?.errorMessage || 'Something went wrong', type: 'danger' });
    } else {
      hide();
    }

    setLockSave(false);
  };

  const updateData = (key, value) => {
    const newData = Object.assign({}, data);
    newData[key] = value;
    setData(newData);
  };

  // useEffect(() => {
  //   if (data?.part?.partNumber || data?.part?.partDrawingNumber) {
  //     setDisableSave(false);
  //   } else {
  //     setDisableSave(true);
  //   }
  // }, [data]);

  useEffect(() => {
    (async () => {
      if (selectedNCFileId) {
        const date = { h: '0', m: '0', s: '0' };

        const ncFile = await getNCFileById(selectedNCFileId);
        if (ncFile) {
          setData({
            ncFileId: ncFile.ncFileId,
            ncFileName: ncFile.ncFileName,
            ncFileStatus: ncFile.ncFileStatus,
            versions: ncFile.versions,
            part: {
              partId: ncFile.partId,
              partDrawingPosition: ncFile.partDrawingPosition,
              partNumber: ncFile.partNumber,
              partDrawingNumber: ncFile.partDrawingNumber,
              partDescription: ncFile.partDescription,
              partDrawingRevision: ncFile.partDrawingRevision,
            },
            ideal: {
              timeType: 'manual',
              runtime: convertTimeLetter(ncFile.runTimePerPcsSecond) || date,
              changeTime: convertTimeLetter(ncFile.changeTimePerPcsSecond) || date,
              setupTime: convertTimeLetter(ncFile.setupTimeSecond) || date,
            },
            paid: {
              timeType: 'manual',
              runtime: date,
              changeTime: date,
              setupTime: date,
            },
          });
          setLoaded(true);
        } else {
          return onBackPress();
        }

        const tools = await getTools({ ncFileId: selectedNCFileId });
        if (tools) {
          setTools(tools);
        }
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (partId && loaded) {
        const resParts = await partsSearch({
          data: {
            search: '',
            top: SEARCH_PAGE_SIZE,
            skip: 0,
          },
          filter: { oDataFilter: `PartId eq ${partId}` },
          select: null,
          resetHistory: true,
          ignoreHistory: true,
        });
        if (resParts?.searchResult?.length) {
          const part = resParts.searchResult[0];
          setData({
            ...data,
            part: {
              partId: part.partId,
              partDrawingPosition: part.position,
              partNumber: part.partName,
              partDrawingNumber: part.drawing,
              partDescription: part.description,
              partDrawingRevision: part.revision,
            },
          });
        }
      }
    })();
  }, [loaded]);

  const baseHeight = 0;
  const animation = {
    setupTime: useRef(new Animated.Value(baseHeight)).current,
    changeTime: useRef(new Animated.Value(baseHeight)).current,
    runtime: useRef(new Animated.Value(baseHeight)).current,
  };

  const openKey = (key) => {
    if (isWeb) {
      return;
    }

    Object.keys(animation).map((k) => {
      if (k !== key) {
        Animated.timing(animation[k], {
          toValue: baseHeight,
          duration: 500,
          useNativeDriver: false,
        }).start();
      }
    });
    const big = scale(200);
    if (animation[key] && animation[key]._value == big) {
      Animated.timing(animation[key], {
        toValue: baseHeight,
        duration: 500,
        useNativeDriver: false,
      }).start();
    } else {
      if (animation[key]) {
        Animated.timing(animation[key], {
          toValue: big,
          duration: 500,
          useNativeDriver: false,
        }).start();
      }
    }
  };

  if (!loaded) {
    return (
      <View style={{ minHeight: scale(790) }}>
        <Loading {...{ background: C('Background/Light/Detail') }} />
      </View>
    );
  }

  const levelTabs = [
    {
      name: 'Part',
      key: 'part',
      value: data.part.partNumber,
      buttonType: !(data.part.partNumber || data.part.partDrawingNumber),
      buttonText: 'Add Part Information',
    },
    {
      name: 'NC File',
      key: 'ncFile',
      value: data.ncFileName,
    },
    {
      name: 'Tools',
      key: 'tools',
      value: tools?.length,
      buttonType: !tools?.length,
      buttonText: 'Add Tool Information',
    },
  ];

  const renderBlock = () => {
    const props = { data, updateData, openKey, animation, C, getPartsSearch, setData, isPhone };

    switch (selectedLevel) {
      case 'ncFile':
        return (
          <NCFileBlock
            {...{
              ...props,
              data,
              setData,
              updateData,
            }}
          />
        );
      case 'part':
        return <PartBlock {...props} />;
      case 'tools':
        return (
          <ClampingTool
            {...{
              ...props,
              largeView: false,
              route: { params: { ncFile: data.ncFileName, ncFileId: selectedNCFileId } },
            }}
          />
        );
    }
  };

  return (
    <View
      testID={TEST_IDS.popups.linkBatchWithoutERP}
      style={{
        width: '100%',
        minHeight: scale(790),
        flex: 1,
        backgroundColor: C(mainBg ?? modalBg),
        alignItems: 'center',
      }}
    >
      <View style={{ width: '100%', flex: 1 }}>
        <Header
          {...{
            C,
            rightAction: saveData,
            rightActionText: 'Save',
            rightActionLock: lockSave,
            rightActionDisable: disableSave,
            onBackPress: hide,
            mainBg,
          }}
        />
        <View
          style={[
            {
              height: scale(50),
              paddingHorizontal: scale(18),
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: isPhone ? scale(10) : scale(28),
            },
          ]}
        >
          <LevelSelector
            {...{
              selectedLevel,
              setSelectedLevel,
              tabs: levelTabs,
              big: isTabletOrWeb,
              selectedItemBG: isPhoneNative ? 'Selected/Light/Primary' : 'Selected/Light/Slider Add',
              selectedColor: isPhoneNative ? 'Button/Light/Primary Text' : 'Selected/Light/Slider Add Text',
            }}
          />
        </View>
        <View style={{ width: '100%', flex: 1, flexDirection: 'row', justifyContent: 'center', paddingHorizontal: scale(18) }}>
          {!isPhone && (
            <View
              style={{
                width: '50%',
              }}
            >
              <NCFile
                {...{
                  C,
                  selectedItem: { machineStatus: data },
                  showNcFileButtonCallBackOnPress: hide,
                  ncFileId: data?.ncFileId,
                  ncFileName: data?.ncFileName,
                  isOperatorPanel,
                  isNcFileContentAvailable,
                  shouldTakeNCFileDataFromMachine,
                }}
              />
            </View>
          )}
          <View
            style={{
              flex: 1,
            }}
          >
            <View
              style={{
                width: '100%',
                height: '100%',
                flex: 1,
              }}
            >
              <View style={{ width: '100%', flex: 1, flexDirection: 'row' }}>{renderBlock()}</View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
});

export default connect(({ imageHandling, clampingTools, manufacture: { selectedNCFile } }) => ({ imageHandling, clampingTools, selectedNCFile }), {
  addProductionInformation,
  addImage,
  startQueue,
  updateMachinePI,
  getMachine,
  getEvent,
  clearState,
  getTools,
  getPartsSearch,
  setMachineDetailsLevel,
  getNCFileById,
  addProductionInformationV2,
  partsSearch,
})(ColorWrapper(Modal));
